import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { getSettingsSection, saveSettingsSection } from '../../../../services/settingsService';
import {
  FaPalette, FaImage, FaGlobe, FaCheckCircle, FaInfoCircle
} from 'react-icons/fa';

const BRAND_COLOURS = [
  { label: 'CloudSafe Blue', value: '#1A80E5' },
  { label: 'Emerald', value: '#059669' },
  { label: 'Violet', value: '#7c3aed' },
  { label: 'Rose', value: '#e11d48' },
  { label: 'Amber', value: '#d97706' },
  { label: 'Slate', value: '#475569' },
];

function BrandingSettings() {
  const [branding, setBranding] = useState({
    logo: null,
    primaryColor: '#1a80e5',
    secondaryColor: '#637587',
    favicon: null,
    companyName: '',
    tagline: '',
  });
  const [saving, setSaving] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);

  useEffect(() => {
    getSettingsSection('branding')
      .then((data) => {
        if (data && Object.keys(data).length > 0) {
          setBranding((prev) => ({ ...prev, ...data }));
        }
      })
      .catch(() => {});
  }, []);

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    const file = files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file', { autoClose: 4000 });
      return;
    }
    if (name === 'logo') {
      const reader = new FileReader();
      reader.onload = (ev) => setLogoPreview(ev.target.result);
      reader.readAsDataURL(file);
    }
    setBranding(prev => ({ ...prev, [name]: file }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setBranding(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        primaryColor: branding.primaryColor,
        secondaryColor: branding.secondaryColor,
        logo: branding.logo?.name || null,
        favicon: branding.favicon?.name || null,
        companyName: branding.companyName,
        tagline: branding.tagline,
      };
      await saveSettingsSection('branding', payload);
      toast.success('Branding settings saved', { autoClose: 3000 });
    } catch (error) {
      toast.error(error.message || 'Failed to save branding settings', { autoClose: 4000 });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="stg-max-form">
      <div className="stg-page-header-row">
        <div className="stg-page-icon stg-page-icon-purple"><FaPalette /></div>
        <div>
          <h1 className="stg-page-title">Branding Settings</h1>
          <p className="stg-page-subtitle">Customise colours, logos, and company identity across the platform.</p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="stg-section">
          <h2 className="stg-section-title">Company Identity</h2>
          <div className="stg-form-group">
            <label htmlFor="companyName" className="stg-form-label">Company / Organisation Name</label>
            <input type="text" id="companyName" name="companyName" className="stg-input" value={branding.companyName} onChange={handleChange} placeholder="e.g. Apex Property Management" />
          </div>
          <div className="stg-form-group">
            <label htmlFor="tagline" className="stg-form-label">Tagline (optional)</label>
            <input type="text" id="tagline" name="tagline" className="stg-input" value={branding.tagline} onChange={handleChange} placeholder="e.g. Building Safety, Simplified" />
          </div>
        </div>

        <div className="stg-section">
          <h2 className="stg-section-title">Logo &amp; Favicon</h2>
          <div className="stg-form-group">
            <label htmlFor="logo" className="stg-form-label">Company Logo</label>
            <div className="stg-upload-row">
              <div className="stg-logo-preview">
                {logoPreview ? <img src={logoPreview} alt="Logo preview" /> : <div className="stg-logo-placeholder"><FaImage /></div>}
              </div>
              <div style={{ flex: 1 }}>
                <input type="file" id="logo" name="logo" className="stg-input stg-input-file" onChange={handleFileChange} accept="image/*" />
                <p className="stg-help-text">PNG or SVG, recommended 200x50px. Will appear in all platform headers.</p>
              </div>
            </div>
          </div>
          <div className="stg-form-group">
            <label htmlFor="favicon" className="stg-form-label">Favicon</label>
            <input type="file" id="favicon" name="favicon" className="stg-input stg-input-file" onChange={handleFileChange} accept="image/*" />
            <p className="stg-help-text">PNG or ICO, recommended 32x32px. Shown in browser tabs.</p>
          </div>
        </div>

        <div className="stg-section">
          <h2 className="stg-section-title">Colour Theme</h2>
          <div className="stg-form-group">
            <label className="stg-form-label">Quick Select</label>
            <div className="stg-swatches">
              {BRAND_COLOURS.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  className={`stg-swatch-btn ${branding.primaryColor === c.value ? 'selected' : ''}`}
                  style={{ background: c.value }}
                  title={c.label}
                  onClick={() => setBranding(prev => ({ ...prev, primaryColor: c.value }))}
                >
                  {branding.primaryColor === c.value && <FaCheckCircle />}
                </button>
              ))}
            </div>
          </div>

          <div className="stg-color-row">
            <div className="stg-form-group">
              <label htmlFor="primaryColor" className="stg-form-label">Primary Colour</label>
              <div className="stg-color-input-wrap">
                <input type="color" id="primaryColor" name="primaryColor" className="stg-color-swatch" value={branding.primaryColor} onChange={handleChange} />
                <input type="text" value={branding.primaryColor} onChange={handleChange} name="primaryColor" className="stg-color-text" maxLength={7} />
              </div>
            </div>
            <div className="stg-form-group">
              <label htmlFor="secondaryColor" className="stg-form-label">Secondary Colour</label>
              <div className="stg-color-input-wrap">
                <input type="color" id="secondaryColor" name="secondaryColor" className="stg-color-swatch" value={branding.secondaryColor} onChange={handleChange} />
                <input type="text" value={branding.secondaryColor} onChange={handleChange} name="secondaryColor" className="stg-color-text" maxLength={7} />
              </div>
            </div>
          </div>

          <div className="stg-preview">
            <div className="stg-preview-label"><FaInfoCircle /> Preview</div>
            <div className="stg-preview-bar" style={{ background: branding.primaryColor }}>
              <span className="stg-preview-logo">{branding.companyName || 'Your Company'}</span>
              <button type="button" className="stg-preview-btn" style={{ background: 'rgba(255,255,255,0.2)', color: '#fff' }}>Dashboard</button>
            </div>
            <div className="stg-preview-cta-row">
              <button type="button" className="stg-preview-cta" style={{ background: branding.primaryColor, color: '#fff' }}>Primary Action</button>
              <button type="button" className="stg-preview-cta stg-preview-cta-sec" style={{ color: branding.secondaryColor, borderColor: branding.secondaryColor }}>Secondary</button>
            </div>
          </div>
        </div>

        <div className="stg-notice">
          <FaGlobe />
          <div>
            <strong>Full white-label branding coming soon</strong>
            <p>Custom domain support, email template customisation, and per-property branding overrides are planned for upcoming releases.</p>
          </div>
        </div>

        <div className="stg-actions" style={{ justifyContent: 'flex-start' }}>
          <button type="submit" className="stg-btn stg-btn-primary" disabled={saving}>
            {saving ? 'Saving\u2026' : 'Save Branding'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default BrandingSettings;
